import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Card,
  CardBody,
  Container,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  VStack,
  Text,
  Grid,
  GridItem,
  Stat,
  StatLabel,
  StatNumber,
  Badge,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Icon,
  Center,
  HStack,
} from "@chakra-ui/react";
import { SearchIcon, DownloadIcon } from "@chakra-ui/icons";
import { get_all_customer_list } from "../../redux/customerSlice";
import { get_loan_list } from "../../redux/LoanSlice";
import GasCylinderLoader from "../../components/GasCylinderLoader";
import SearchableDropdown from "../../components/SearchableDropdown"; // Import the new component

const LoanListComponent = () => {
  const dispatch = useDispatch();
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [showResults, setShowResults] = useState(false);
  const [excelUrl, setExcelUrl] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [totalSecurityDeposit, setTotalSecurityDeposit] = useState(0);
const [isFirstSearchDone, setIsFirstSearchDone] = useState(false);

  const { all_customer_list, all_loading } = useSelector(
    (state) => state.customers
  );
  const { loanList, loading } = useSelector((state) => state.loan);

  // Fetch all customers on mount
  useEffect(() => {
    dispatch(get_all_customer_list({ page: 1, rows: 10000 }));
  }, [dispatch]);

const handleSearch = () => {
  if (selectedCustomer) {
    setIsFirstSearchDone(true);
    fetchLoanData(selectedCustomer);
  } else {
    alert("Please select a customer first");
  }
};


  const fetchLoanData = (customerId) => {
  dispatch(get_loan_list({ userid: customerId, action: "loan_list" }))
    .then((response) => {
      if (response.payload?.excel_url) setExcelUrl(response.payload.excel_url);
      if (response.payload?.pdf_url) setPdfUrl(response.payload.pdf_url);
      if (response.payload?.total_security_deposit)
        setTotalSecurityDeposit(response.payload.total_security_deposit);
    });
  setShowResults(true);
};


  // Download Excel file
  const handleDownloadExcel = () => {
    if (excelUrl) {
      const link = document.createElement('a');
      link.href = excelUrl;
      link.setAttribute('download', 'Loan_Report.xlsx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      alert("Excel file not available");
    }
  };

  const calculateProductTotals = (orders) => {
    return orders.reduce(
      (acc, order) => {
        const count = Number(order.count) || 0;
        const deliveredQty = Number(order.delivered_quantity) || 0;
        const emptyReceived = Number(order.empty_received) || 0;
        const equipmentUsed = Number(order.equipment_used_qty) || 0;
        const sdCount = Number(order.sd_count) || 0;
        const securityDepositPerUnit = Number(order.security_deposit_per_unit) || 0;
        const securityTotal = securityDepositPerUnit * sdCount;

        return {
          totalCount: acc.totalCount + count,
          totalDelivered: acc.totalDelivered + deliveredQty,
          totalEmptyReceived: acc.totalEmptyReceived + emptyReceived,
          totalEquipmentUsed: acc.totalEquipmentUsed + equipmentUsed,
          loanBalance: acc.loanBalance + (deliveredQty - emptyReceived),
          totalSdCount: acc.totalSdCount + sdCount,
          totalSecurity: acc.totalSecurity + securityTotal,
        };
      },
      {
        totalCount: 0,
        totalDelivered: 0,
        totalEmptyReceived: 0,
        totalEquipmentUsed: 0,
        loanBalance: 0,
        totalSdCount: 0,
        totalSecurity: 0,
      }
    );
  };

  // Handler for customer selection
 const handleCustomerChange = (customer) => {
  setSelectedCustomer(customer.id);
  setExcelUrl("");
  setPdfUrl("");
};


  useEffect(() => {
  if (selectedCustomer && isFirstSearchDone) {
    fetchLoanData(selectedCustomer);
  }
}, [selectedCustomer]);


  return (
    <Container maxW="container.xl" py={6}>
      <VStack spacing={6} align="stretch">
        {/* Header with Download Buttons */}
        <Flex justify="space-between" align="center" wrap="wrap">
          <Heading size="lg" color="gray.700">
            Loan Details
          </Heading>

          {excelUrl && (
            <HStack spacing={3}>
              <Button
                onClick={handleDownloadExcel}
                colorScheme="green"
                size="md"
                leftIcon={<DownloadIcon />}
                variant="solid"
              >
                Download Excel
              </Button>
            </HStack>
          )}
        </Flex>

        {/* Search Section */}
        <Card w="50%">
          <CardBody>
            <Flex gap={4} align="flex-end" flexWrap="wrap">
              <FormControl flex={1}>
                <FormLabel>Select Customer</FormLabel>

                {/* Using the new SearchableDropdown component */}
                <SearchableDropdown
                  options={all_customer_list}
                  value={selectedCustomer}
                  onChange={handleCustomerChange}
                  placeholder="Select Customer"
                  searchPlaceholder="Search by name, mobile, or email..."
                  getOptionLabel={(customer) => customer.name || customer.mobile}
                  getOptionValue={(customer) => customer.id}
                  getOptionSubLabel={(customer) => {
                    const parts = [customer.mobile];
                    if (customer.email) parts.push(customer.email);
                    return parts.join(" • ");
                  }}
                  filterOptions={(options, searchTerm) => {
                    const term = searchTerm.toLowerCase();
                    return options.filter(
                      (c) =>
                        c.name?.toLowerCase().includes(term) ||
                        c.mobile?.toLowerCase().includes(term) ||
                        c.email?.toLowerCase().includes(term)
                    );
                  }}
                  isDisabled={all_loading}
                  size="lg"
                />
              </FormControl>

              <Button
                leftIcon={<SearchIcon />}
                colorScheme="blue"
                onClick={handleSearch}
                isDisabled={!selectedCustomer || loading}
                isLoading={loading}
                loadingText="Searching"
                size="lg"
              >
                Search
              </Button>
            </Flex>
          </CardBody>
        </Card>

        {/* Results Section */}
        {showResults && (
          <Box>
            {loading ? (
              <GasCylinderLoader
                size="sm"
                loadingText="Loan details loading"
              />
            ) : loanList && loanList.length > 0 ? (
              <VStack spacing={6} align="stretch">
                {loanList.map((product, index) => {
                  const totals = calculateProductTotals(product.orders);
                  return (
                    <Card key={index} variant="outline" borderWidth={2}>
                      <Box bg="blue.500" color="white" p={4} borderTopRadius="md">
                        <Heading size="md">{product.product_name}</Heading>
                        <Text fontSize="sm" opacity={0.9}>
                          {product.brand} - {product.productid}
                        </Text>
                      </Box>

                      <CardBody>
                        <Grid
                          templateColumns={{
                            base: "repeat(2, 1fr)",
                            md: "repeat(6, 1fr)",
                          }}
                          gap={4}
                          mb={6}
                          p={4}
                          bg="gray.50"
                          borderRadius="md"
                        >
                          <GridItem>
                            <Stat textAlign="center">
                              <StatLabel color="gray.600" fontSize="sm">
                                Total Orders
                              </StatLabel>
                              <StatNumber color="gray.800" fontSize="2xl">
                                {totals.totalCount}
                              </StatNumber>
                            </Stat>
                          </GridItem>
                          <GridItem>
                            <Stat textAlign="center">
                              <StatLabel color="gray.600" fontSize="sm">
                                Delivered
                              </StatLabel>
                              <StatNumber color="green.600" fontSize="2xl">
                                {totals.totalDelivered}
                              </StatNumber>
                            </Stat>
                          </GridItem>
                          <GridItem>
                            <Stat textAlign="center">
                              <StatLabel color="gray.600" fontSize="sm">
                                Empties Received
                              </StatLabel>
                              <StatNumber color="blue.600" fontSize="2xl">
                                {totals.totalEmptyReceived}
                              </StatNumber>
                            </Stat>
                          </GridItem>
                          <GridItem>
                            <Stat textAlign="center">
                              <StatLabel color="gray.600" fontSize="sm">
                                Loan Balance
                              </StatLabel>
                              <StatNumber
                                color={totals.loanBalance > 0 ? "red.600" : "green.600"}
                                fontSize="2xl"
                              >
                                {totals.loanBalance}
                              </StatNumber>
                            </Stat>
                          </GridItem>
                          <GridItem>
                            <Stat textAlign="center">
                              <StatLabel color="gray.600" fontSize="sm">
                                Total SD Count
                              </StatLabel>
                              <StatNumber color="orange.600" fontSize="2xl">
                                {totals.totalSdCount}
                              </StatNumber>
                            </Stat>
                          </GridItem>
                          <GridItem>
                            <Stat textAlign="center">
                              <StatLabel color="gray.600" fontSize="sm">
                                Total Security Deposit
                              </StatLabel>
                              <StatNumber color="green.600" fontSize="2xl">
                                ₹{totals.totalSecurity.toFixed(2)}
                              </StatNumber>
                            </Stat>
                          </GridItem>
                        </Grid>

                        {/* Orders Table */}
                        <Box overflowX="auto">
                          <Table variant="simple" size="sm">
                            <Thead bg="gray.100">
                              <Tr>
                                <Th>Order ID</Th>
                                <Th>Order Date</Th>
                                <Th isNumeric>Quantity</Th>
                                <Th isNumeric>Delivered</Th>
                                <Th isNumeric>Empty Received</Th>
                                <Th>Extra Picked Product</Th>
                                <Th isNumeric>Customer Owned Equipment</Th>
                                <Th isNumeric>Loan Equipment</Th>
                                <Th isNumeric>Sd Count</Th>
                                <Th isNumeric>Security Value</Th>
                                <Th>Delivery Date</Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {product.orders.map((order, i) => {
                                const balance =
                                  Number(order.delivered_quantity) - Number(order.empty_received);
                                return (
                                  <Tr key={i} _hover={{ bg: "gray.50" }}>
                                    <Td>{order.orderid}</Td>
                                    <Td>
                                      {new Date(order.ordered_datetime).toLocaleString("en-IN")}
                                    </Td>
                                    <Td isNumeric>{order.count}</Td>
                                    <Td isNumeric color="green.600">
                                      {order.delivered_quantity}
                                    </Td>
                                    <Td isNumeric color="blue.600">{order.empty_received}</Td>
                                    <Td>{order.extra_product_name || "NA"}</Td>
                                    <Td isNumeric>{order.extra_received_empty}</Td>
                                    <Td isNumeric>{order.equipment_used_qty}</Td>
                                    <Td isNumeric>{order.sd_count || "NA"}</Td>
                                    <Td isNumeric color="purple.600">
                                      ₹{order.sd_value || "0.00"}
                                    </Td>
                                    <Td>
                                      {order.deliver_datetime ? (
                                        new Date(order.deliver_datetime).toLocaleString("en-IN")
                                      ) : (
                                        <Badge colorScheme="orange">Pending</Badge>
                                      )}
                                    </Td>
                                  </Tr>
                                );
                              })}
                            </Tbody>
                          </Table>
                        </Box>
                      </CardBody>
                    </Card>
                  );
                })}
              </VStack>
            ) : (
              <Card>
                <CardBody>
                  <Center py={12}>
                    <VStack spacing={4}>
                      <Icon viewBox="0 0 24 24" boxSize={12} color="gray.400">
                        <path
                          fill="currentColor"
                          d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
                        />
                      </Icon>
                      <Heading size="md" color="gray.900">
                        No loan records found
                      </Heading>
                      <Text color="gray.500" fontSize="sm">
                        This customer doesn't have any loan records.
                      </Text>
                    </VStack>
                  </Center>
                </CardBody>
              </Card>
            )}
          </Box>
        )}
      </VStack>
    </Container>
  );
};

export default LoanListComponent;